import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { useCallback } from 'react';
import { combineCreatePaymentMethodAndConfirmCardHooks, createCreditCardCustomError, createFieldCustomValidationError, createSuccessResult, FieldNames, isFailure, UIAssociations, useConfirmCardPaymentWithStripeTransaction, useConfirmCardSetupTransaction, useCreatePaymentMethodTransaction, useTryCatchToExceptionResultWrapper } from '@atlassian/commerce-credit-card-base';
import { useCommerceFetch } from '@atlassian/commerce-environment';
import { useIntentSecretURL } from '../../services/hams';
import { useBrandError } from '../../services/hams/brand-validation';
import { createSetupIntentClientSecret } from '../../services/hams/requests';
import { useHAMSGateway } from '../form-state';

var useBrandValidator = function useBrandValidator() {
  var brandError = useBrandError();
  var validate = useCallback(function () {
    if (brandError !== undefined) {
      return createFieldCustomValidationError(brandError.message, FieldNames.NUMBER);
    }

    return createSuccessResult(null);
  }, [brandError]);
  return useTryCatchToExceptionResultWrapper(validate);
};

var useCreateSetupIntentClientSecret = function useCreateSetupIntentClientSecret() {
  var fetch = useCommerceFetch();
  var url = useIntentSecretURL();
  var gateway = useHAMSGateway();
  var createSetupIntentClientSecretCallback = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var setupIntentSecretResult;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return createSetupIntentClientSecret(fetch, url, {
              gateway: gateway
            });

          case 2:
            setupIntentSecretResult = _context.sent;

            if (!isFailure(setupIntentSecretResult)) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", createCreditCardCustomError('Setup intent secret was invalid', {
              type: UIAssociations.BindingLevels.NONE
            }));

          case 5:
            return _context.abrupt("return", setupIntentSecretResult);

          case 6:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [fetch, url, gateway]);
  return createSetupIntentClientSecretCallback;
};
/**
 * Consider using {@link useHAMSOneTimePaymentConfirm} or {@link useHAMSRenewablePaymentConfirm} if you do not need
 * manual control over when certain Stripe objects are created.
 * @see https://stripe.com/docs/js/payment_methods/create_payment_method
 */


export var useHAMSCreatePaymentMethod = function useHAMSCreatePaymentMethod() {
  var validate = useBrandValidator();
  return useCreatePaymentMethodTransaction(useCallback(function (create) {
    return /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var validationResult,
          _args2 = arguments;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return validate();

            case 2:
              validationResult = _context2.sent;

              if (!isFailure(validationResult)) {
                _context2.next = 5;
                break;
              }

              return _context2.abrupt("return", validationResult);

            case 5:
              return _context2.abrupt("return", create.apply(void 0, _args2));

            case 6:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));
  }, [validate]));
};
export var useHAMSConfirmCardSetup = function useHAMSConfirmCardSetup() {
  var createSetupIntentClientSecretCallback = useCreateSetupIntentClientSecret();
  return useConfirmCardSetupTransaction(useCallback(function (confirm) {
    return /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(paymentMethodId, options) {
        var setupIntentSecretResult;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return createSetupIntentClientSecretCallback();

              case 2:
                setupIntentSecretResult = _context3.sent;

                if (!isFailure(setupIntentSecretResult)) {
                  _context3.next = 5;
                  break;
                }

                return _context3.abrupt("return", setupIntentSecretResult);

              case 5:
                return _context3.abrupt("return", confirm(setupIntentSecretResult.payload.setupIntentClientSecret, paymentMethodId, options));

              case 6:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }));

      return function (_x, _x2) {
        return _ref3.apply(this, arguments);
      };
    }();
  }, [createSetupIntentClientSecretCallback]));
};
/**
 * Use this if you want to create a payment method that can be transacted with multiple times.
 * @see {@link useHAMSRenewablePaymentConfirm}
 * @see https://stripe.com/docs/js/payment_methods/create_payment_method
 * @see https://stripe.com/docs/js/setup_intents/confirm_card_setup
 */

export var useHAMSRenewablePaymentConfirm = combineCreatePaymentMethodAndConfirmCardHooks(useHAMSCreatePaymentMethod, useHAMSConfirmCardSetup);
/**
 * Use this if you want to create a payment method for a one-time transaction.
 * @see {@link useHAMSRenewablePaymentConfirm}
 * @see https://stripe.com/docs/js/payment_methods/create_payment_method
 * @see https://stripe.com/docs/js/setup_intents/confirm_card_setup
 */

export var useHAMSOneTimePaymentConfirm = useHAMSRenewablePaymentConfirm;
export var useHAMSConfirmCardPaymentWithStripe = function useHAMSConfirmCardPaymentWithStripe() {
  return useConfirmCardPaymentWithStripeTransaction(function (confirm) {
    return confirm;
  });
};